<script setup>
// Meta baglantilari: kayitli jetonlar, bagli reklam hesaplari, isletme
// sayfalari ve Instagram hesabi.
//
// Bu sayfa bilerek hesap secicisine bagimli degil: jeton gecersizse reklam
// hesabi listesi de bos doner, yani kullanicinin once buradan jetonunu
// duzeltebilmesi gerekir.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { aracCalistir, diziyeCevir } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();

const hesapKimligi = computed(() => route.params.accountId);

// --- Okunur gosterim yardimcilari ----------------------------------------
// Meta alan adlarini snake_case, tarihleri de ISO dizesi ya da Unix saniyesi
// olarak dondurur. Ham anahtar ("followers_count") ve ham damga kullaniciya
// hicbir sey anlatmadigi icin ekranda cevirisi olan anahtari Turkce basliga,
// tarih alanlarini yerel bicime ceviriyoruz.
const ALAN_ONEKI = 'META_ADS.AYARLAR.BAGLANTILAR.ALAN.';

// Cevirisi olmayan anahtari uydurmak yerine oldugu gibi birakiyoruz; vue-i18n
// karsiligi yoksa anahtarin kendisini dondurdugu icin boyle ayirt ediliyor.
const alanEtiketi = anahtar => {
  const yol = ALAN_ONEKI + anahtar.toUpperCase();
  const metin = t(yol);
  return metin === yol ? anahtar : metin;
};

const TARIH_ALANLARI = ['timestamp', 'created_time', 'updated_time', 'expires_at'];

const tarihBicimle = deger => {
  const ham = String(deger);
  // Unix saniyesi 10 hanelidir; geri kalani ISO dizesi olarak okunuyor.
  const zaman = /^\d{10}$/.test(ham) ? new Date(Number(ham) * 1000) : new Date(ham);
  return Number.isNaN(zaman.getTime())
    ? ham
    : zaman.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

// Ic ice nesne ya da dizi tek satira sigmaz. Ham JSON basmak yerine dizide oge
// sayisini, nesnede varsa tanitici alani gosteriyoruz.
const icerikOzeti = deger => {
  if (Array.isArray(deger)) {
    return t('META_ADS.AYARLAR.ORTAK.OGE_SAYISI', { sayi: deger.length });
  }
  const tanitici = deger.name || deger.display_string || deger.id;
  if (tanitici) return String(tanitici);
  return t('META_ADS.AYARLAR.ORTAK.ALAN_SAYISI', { sayi: Object.keys(deger).length });
};

const alanDegeri = (anahtar, deger) => {
  if (deger === null || deger === '') return '—';
  if (typeof deger === 'boolean') {
    return deger ? t('META_ADS.AYARLAR.ORTAK.EVET') : t('META_ADS.AYARLAR.ORTAK.HAYIR');
  }
  if (typeof deger === 'object') return icerikOzeti(deger);
  if (TARIH_ALANLARI.includes(anahtar)) return tarihBicimle(deger);
  return String(deger);
};

// Nesne donduren araclarda tabloya gerek yok; duz anahtar/deger listesi yeter.
const alanlaraCevir = veri =>
  Object.entries(veri || {})
    .filter(([, d]) => d !== undefined)
    .map(([anahtar, deger]) => ({
      anahtar,
      etiket: alanEtiketi(anahtar),
      deger: alanDegeri(anahtar, deger),
    }));

// --- Jetonlar -------------------------------------------------------------
const jetonlar = ref([]);
const jetonYukleniyor = ref(false);
const jetonHatasi = ref('');
const yeniJeton = ref('');
const yeniEtiket = ref('');
const kaydediliyor = ref(false);

// MCP jeton kayitlarini farkli adlarla dondurebilir; listeyi kullanilabilir
// kilan tek sey satirdaki kimlik oldugu icin sirayla bakiyoruz.
const jetonKimligi = kayit =>
  kayit?.token_id || kayit?.id || kayit?.label || kayit?.name || '';

const jetonAktifMi = kayit =>
  Boolean(kayit?.is_active ?? kayit?.active ?? kayit?.aktif);

const jetonSutunlari = computed(() => [
  {
    anahtar: 'label',
    baslik: t('META_ADS.AYARLAR.BAGLANTILAR.ETIKET'),
    bicim: (deger, satir) => deger || satir.name || jetonKimligi(satir) || '—',
  },
  {
    anahtar: 'token_id',
    baslik: t('META_ADS.AYARLAR.BAGLANTILAR.KIMLIK'),
    bicim: (deger, satir) => jetonKimligi(satir) || '—',
  },
  {
    anahtar: 'expires_at',
    baslik: t('META_ADS.AYARLAR.BAGLANTILAR.SON_KULLANMA'),
    // Ham ISO damgasi yerine yerel tarih; okunmaz bir dize sutunu kaplamasin.
    bicim: (deger, satir) => {
      const ham = deger || satir.expires;
      return ham ? tarihBicimle(ham) : '—';
    },
  },
  {
    anahtar: 'is_active',
    baslik: t('META_ADS.AYARLAR.BAGLANTILAR.DURUM'),
    bicim: (deger, satir) =>
      jetonAktifMi(satir)
        ? t('META_ADS.AYARLAR.BAGLANTILAR.AKTIF')
        : t('META_ADS.AYARLAR.BAGLANTILAR.PASIF'),
  },
]);

const jetonlariYukle = async () => {
  jetonYukleniyor.value = true;
  jetonHatasi.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'ads_list_tokens');
    jetonlar.value = diziyeCevir(sonuc);
  } catch (e) {
    jetonHatasi.value = e.response?.data?.error || e.message;
  } finally {
    jetonYukleniyor.value = false;
  }
};

const jetonEkle = async () => {
  if (!yeniJeton.value.trim()) return;
  // Kayitli jeton bu kurulumun Meta adina islem yapma yetkisidir; yanlis
  // yapistirilan bir dize sessizce kaydedilmesin diye once soruluyor.
  if (!window.confirm(t('META_ADS.AYARLAR.BAGLANTILAR.JETON_EKLEME_ONAYI'))) return;
  kaydediliyor.value = true;
  jetonHatasi.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'ads_register_token',
      { token: yeniJeton.value.trim(), label: yeniEtiket.value.trim() },
      { onaylandi: true }
    );
    // Jeton bellekte kalmasin; ekranda da bir daha gosterilmiyor.
    yeniJeton.value = '';
    yeniEtiket.value = '';
    useAlert(t('META_ADS.AYARLAR.BAGLANTILAR.JETON_EKLENDI'));
    await jetonlariYukle();
  } catch (e) {
    jetonHatasi.value = e.response?.data?.error || e.message;
  } finally {
    kaydediliyor.value = false;
  }
};

const jetonuAktifYap = async kayit => {
  // Aktif jeton tum Meta Ads sayfalarinin kimligidir; degistirmek acik duran
  // sayfalarin bir anda baska bir yetki setiyle calismasi demek.
  if (!window.confirm(t('META_ADS.AYARLAR.BAGLANTILAR.AKTIF_YAPMA_ONAYI'))) return;
  jetonHatasi.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'ads_set_active_token',
      { token_id: jetonKimligi(kayit) },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.AYARLAR.BAGLANTILAR.AKTIF_DEGISTI'));
    await jetonlariYukle();
  } catch (e) {
    jetonHatasi.value = e.response?.data?.error || e.message;
  }
};

const jetonuSil = async kayit => {
  // Silinen jetonla calisan tum sayfalar aninda yetkisiz kalir, o yuzden onay.
  if (!window.confirm(t('META_ADS.AYARLAR.BAGLANTILAR.SILME_ONAYI'))) return;
  jetonHatasi.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'ads_delete_token',
      { token_id: jetonKimligi(kayit) },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.AYARLAR.BAGLANTILAR.JETON_SILINDI'));
    await jetonlariYukle();
  } catch (e) {
    jetonHatasi.value = e.response?.data?.error || e.message;
  }
};

// --- Bagli reklam hesaplari ----------------------------------------------
const reklamHesaplari = ref([]);
const hesapYukleniyor = ref(false);
const hesapHatasi = ref('');

const reklamHesabiSutunlari = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.AYARLAR.BAGLANTILAR.HESAP_ADI') },
  { anahtar: 'id', baslik: t('META_ADS.AYARLAR.BAGLANTILAR.KIMLIK') },
  { anahtar: 'currency', baslik: t('META_ADS.AYARLAR.BAGLANTILAR.PARA_BIRIMI') },
  {
    anahtar: 'account_status',
    baslik: t('META_ADS.AYARLAR.BAGLANTILAR.DURUM'),
    // Meta durumu sayi olarak verir; 1 disindaki her sey reklam yayinini durdurur.
    bicim: deger =>
      Number(deger) === 1
        ? t('META_ADS.AYARLAR.BAGLANTILAR.HESAP_ETKIN')
        : t('META_ADS.AYARLAR.BAGLANTILAR.HESAP_KISITLI'),
  },
]);

const reklamHesaplariniYukle = async () => {
  hesapYukleniyor.value = true;
  hesapHatasi.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'ads_get_ad_accounts');
    reklamHesaplari.value = diziyeCevir(sonuc);
  } catch (e) {
    hesapHatasi.value = e.response?.data?.error || e.message;
  } finally {
    hesapYukleniyor.value = false;
  }
};

// --- Isletme sayfalari ----------------------------------------------------
const isletmeKimligi = ref('');
const sayfalar = ref([]);
const sayfaYukleniyor = ref(false);
const sayfaHatasi = ref('');

const sayfaSutunlari = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.AYARLAR.BAGLANTILAR.SAYFA_ADI') },
  { anahtar: 'id', baslik: t('META_ADS.AYARLAR.BAGLANTILAR.KIMLIK') },
  { anahtar: 'category', baslik: t('META_ADS.AYARLAR.BAGLANTILAR.KATEGORI') },
]);

const sayfalariYukle = async () => {
  if (!isletmeKimligi.value.trim()) return;
  sayfaYukleniyor.value = true;
  sayfaHatasi.value = '';
  try {
    const sonuc = await aracCalistir(
      hesapKimligi.value,
      'ads_get_pages_for_business',
      { business_id: isletmeKimligi.value.trim() }
    );
    sayfalar.value = diziyeCevir(sonuc);
  } catch (e) {
    sayfaHatasi.value = e.response?.data?.error || e.message;
  } finally {
    sayfaYukleniyor.value = false;
  }
};

// --- Instagram ------------------------------------------------------------
const sayfaKimligi = ref('');
const instagramOzeti = ref('');
const instagramAlanlari = ref([]);
const instagramKimligi = ref('');
const gonderiler = ref([]);
const igYukleniyor = ref(false);
const igHatasi = ref('');

const gonderiSutunlari = computed(() => [
  { anahtar: 'caption', baslik: t('META_ADS.AYARLAR.BAGLANTILAR.ACIKLAMA') },
  { anahtar: 'media_type', baslik: t('META_ADS.AYARLAR.BAGLANTILAR.TUR') },
  {
    anahtar: 'timestamp',
    baslik: t('META_ADS.AYARLAR.BAGLANTILAR.TARIH'),
    bicim: deger => (deger ? tarihBicimle(deger) : '—'),
  },
]);

const instagramiYukle = async () => {
  if (!sayfaKimligi.value.trim()) return;
  igYukleniyor.value = true;
  igHatasi.value = '';
  instagramAlanlari.value = [];
  gonderiler.value = [];
  // Yeni sorgu bos donerse ya da hata verirse onceki sayfanin Instagram
  // kimligi elde kalmasin; kalirsa "Gonderiler" yanlis hesabin medyasini ceker.
  instagramKimligi.value = '';
  try {
    const sonuc = await aracCalistir(
      hesapKimligi.value,
      'ads_get_instagram_account',
      { page_id: sayfaKimligi.value.trim() }
    );
    instagramOzeti.value = sonuc.ozet || '';
    const veri = Array.isArray(sonuc.veri) ? sonuc.veri[0] : sonuc.veri;
    instagramAlanlari.value = alanlaraCevir(veri);
    instagramKimligi.value = veri?.id || veri?.instagram_business_account?.id || '';
  } catch (e) {
    igHatasi.value = e.response?.data?.error || e.message;
  } finally {
    igYukleniyor.value = false;
  }
};

const gonderileriYukle = async () => {
  if (!instagramKimligi.value) return;
  igYukleniyor.value = true;
  igHatasi.value = '';
  try {
    const sonuc = await aracCalistir(
      hesapKimligi.value,
      'ads_get_instagram_media',
      { instagram_account_id: instagramKimligi.value }
    );
    gonderiler.value = diziyeCevir(sonuc);
  } catch (e) {
    igHatasi.value = e.response?.data?.error || e.message;
  } finally {
    igYukleniyor.value = false;
  }
};

onMounted(() => {
  jetonlariYukle();
  reklamHesaplariniYukle();
});
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Jetonlar -->
    <section class="border rounded-lg border-n-weak">
      <header class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.AYARLAR.BAGLANTILAR.JETONLAR') }}
        </h2>
        <span class="grow" />
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-refresh-cw"
          :label="t('META_ADS.AYARLAR.BAGLANTILAR.YENILE')"
          @click="jetonlariYukle"
        />
      </header>

      <p v-if="jetonHatasi" class="px-4 py-3 text-sm text-n-ruby-11">
        {{ jetonHatasi }}
      </p>

      <AracTablosu
        :satirlar="jetonlar"
        :sutunlar="jetonSutunlari"
        :yukleniyor="jetonYukleniyor"
        :bos-metin="t('META_ADS.AYARLAR.BAGLANTILAR.JETON_YOK')"
      >
        <template #eylemler="{ satir }">
          <Button
            v-if="!jetonAktifMi(satir)"
            variant="faded"
            size="sm"
            :label="t('META_ADS.AYARLAR.BAGLANTILAR.AKTIF_YAP')"
            @click="jetonuAktifYap(satir)"
          />
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-trash-2"
            class="ml-2"
            :label="t('META_ADS.AYARLAR.BAGLANTILAR.SIL')"
            @click="jetonuSil(satir)"
          />
        </template>
      </AracTablosu>

      <div class="flex flex-wrap items-end gap-3 px-4 py-4 border-t border-n-weak">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('META_ADS.AYARLAR.BAGLANTILAR.ETIKET') }}
          <input
            v-model="yeniEtiket"
            type="text"
            class="!mb-0 w-48"
            :placeholder="t('META_ADS.AYARLAR.BAGLANTILAR.ETIKET_IPUCU')"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11 grow">
          {{ t('META_ADS.AYARLAR.BAGLANTILAR.YENI_JETON') }}
          <!-- Jeton omuz ustunden okunabilecek bir sir; parola alani kullaniliyor. -->
          <input
            v-model="yeniJeton"
            type="password"
            autocomplete="off"
            class="!mb-0 min-w-[280px]"
            :placeholder="t('META_ADS.AYARLAR.BAGLANTILAR.JETON_IPUCU')"
          />
        </label>
        <Button
          variant="solid"
          size="sm"
          icon="i-lucide-plus"
          :label="t('META_ADS.AYARLAR.BAGLANTILAR.JETON_EKLE')"
          :disabled="kaydediliyor || !yeniJeton"
          @click="jetonEkle"
        />
      </div>
    </section>

    <!-- Bagli reklam hesaplari -->
    <section class="border rounded-lg border-n-weak">
      <header class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.AYARLAR.BAGLANTILAR.REKLAM_HESAPLARI') }}
        </h2>
        <span class="grow" />
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-refresh-cw"
          :label="t('META_ADS.AYARLAR.BAGLANTILAR.YENILE')"
          @click="reklamHesaplariniYukle"
        />
      </header>

      <p v-if="hesapHatasi" class="px-4 py-3 text-sm text-n-ruby-11">
        {{ hesapHatasi }}
      </p>

      <AracTablosu
        :satirlar="reklamHesaplari"
        :sutunlar="reklamHesabiSutunlari"
        :yukleniyor="hesapYukleniyor"
        :bos-metin="t('META_ADS.AYARLAR.BAGLANTILAR.HESAP_YOK')"
      />
    </section>

    <!-- Isletme sayfalari -->
    <section class="border rounded-lg border-n-weak">
      <header class="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-n-weak">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.AYARLAR.BAGLANTILAR.SAYFALAR') }}
        </h2>
        <span class="grow" />
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('META_ADS.AYARLAR.BAGLANTILAR.ISLETME_KIMLIGI') }}
          <input v-model="isletmeKimligi" type="text" class="!mb-0 w-56" />
        </label>
        <Button
          variant="faded"
          size="sm"
          :label="t('META_ADS.AYARLAR.BAGLANTILAR.GETIR')"
          :disabled="!isletmeKimligi"
          @click="sayfalariYukle"
        />
      </header>

      <p v-if="sayfaHatasi" class="px-4 py-3 text-sm text-n-ruby-11">
        {{ sayfaHatasi }}
      </p>

      <AracTablosu
        :satirlar="sayfalar"
        :sutunlar="sayfaSutunlari"
        :yukleniyor="sayfaYukleniyor"
        :bos-metin="t('META_ADS.AYARLAR.BAGLANTILAR.SAYFA_YOK')"
      />
    </section>

    <!-- Instagram -->
    <section class="border rounded-lg border-n-weak">
      <header class="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-n-weak">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.AYARLAR.BAGLANTILAR.INSTAGRAM') }}
        </h2>
        <span class="grow" />
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('META_ADS.AYARLAR.BAGLANTILAR.SAYFA_KIMLIGI') }}
          <input v-model="sayfaKimligi" type="text" class="!mb-0 w-56" />
        </label>
        <Button
          variant="faded"
          size="sm"
          :label="t('META_ADS.AYARLAR.BAGLANTILAR.GETIR')"
          :disabled="!sayfaKimligi"
          @click="instagramiYukle"
        />
        <Button
          variant="faded"
          size="sm"
          :label="t('META_ADS.AYARLAR.BAGLANTILAR.GONDERILER')"
          :disabled="!instagramKimligi"
          @click="gonderileriYukle"
        />
      </header>

      <p v-if="igHatasi" class="px-4 py-3 text-sm text-n-ruby-11">{{ igHatasi }}</p>

      <p v-if="instagramOzeti" class="px-4 pt-3 text-sm whitespace-pre-line text-n-slate-11">
        {{ instagramOzeti }}
      </p>

      <dl v-if="instagramAlanlari.length" class="grid grid-cols-2 gap-x-6 gap-y-2 px-4 py-4">
        <div
          v-for="alan in instagramAlanlari"
          :key="alan.anahtar"
          class="grid grid-cols-[auto_1fr] gap-x-4 text-sm"
        >
          <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
          <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
        </div>
      </dl>

      <AracTablosu
        v-if="gonderiler.length"
        :satirlar="gonderiler"
        :sutunlar="gonderiSutunlari"
        :yukleniyor="igYukleniyor"
        :bos-metin="t('META_ADS.AYARLAR.BAGLANTILAR.GONDERI_YOK')"
      />
    </section>
  </div>
</template>
